/**
 * Cliente HTTP minimalista. Inyecta el JWT desde localStorage.
 */
const TOKEN_KEY = 'opsapp_token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: any,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function api<T = any>(
  path: string,
  init?: RequestInit & { params?: Record<string, any> },
): Promise<T> {
  const { params, ...rest } = init ?? {};

  let url = path.startsWith('http') ? path : `/api${path.startsWith('/') ? path : `/${path}`}`;
  if (params && Object.keys(params).length > 0) {
    const search = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null && v !== '') {
        search.append(k, String(v));
      }
    }
    const qs = search.toString();
    if (qs) url += (url.includes('?') ? '&' : '?') + qs;
  }

  const headers = new Headers(rest.headers);
  headers.set('Accept', 'application/json');
  if (!headers.has('Content-Type') && rest.body) {
    headers.set('Content-Type', 'application/json');
  }
  const token = getToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(url, { ...rest, headers });

  // 401 → token inválido o expirado, limpiar y redirigir
  if (res.status === 401) {
    clearToken();
    if (window.location.pathname !== '/login') {
      window.location.href = '/login?expired=1';
    }
    throw new ApiError(401, 'No autorizado');
  }

  const contentType = res.headers.get('content-type') ?? '';
  let body: any = null;
  if (contentType.includes('application/json')) {
    body = await res.json().catch(() => null);
  } else {
    body = await res.text().catch(() => null);
  }

  if (!res.ok) {
    const msg =
      (body && typeof body === 'object' && (body.message || body.error)) ||
      (typeof body === 'string' ? body : `Error ${res.status}`);
    throw new ApiError(res.status, msg, body);
  }

  return body as T;
}

/**
 * Descarga un archivo binario (p. ej. .xlsx) de un endpoint autenticado y
 * dispara el "Save as" del navegador. El `api()` normal no sirve porque solo
 * lee JSON/texto.
 */
export async function apiDownload(
  path: string,
  filename: string,
  params?: Record<string, any>,
): Promise<void> {
  let url = path.startsWith('http') ? path : `/api${path.startsWith('/') ? path : `/${path}`}`;
  if (params && Object.keys(params).length > 0) {
    const search = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null && v !== '') search.append(k, String(v));
    }
    const qs = search.toString();
    if (qs) url += (url.includes('?') ? '&' : '?') + qs;
  }

  const headers = new Headers();
  const token = getToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(url, { headers });
  if (res.status === 401) {
    clearToken();
    if (window.location.pathname !== '/login') window.location.href = '/login?expired=1';
    throw new ApiError(401, 'No autorizado');
  }
  if (!res.ok) throw new ApiError(res.status, `Error ${res.status}`);

  const blob = await res.blob();
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objUrl);
}
